import type { ActiveFilter, SearchFilter } from './types';

/**
 * Determines whether the cursor sits inside (or at the edges of) a filter
 * token such as `repo:` or `repo:frontbucket`.
 *
 * A filter token is defined as:
 *   - A known filter label (e.g. `repo:`)
 *   - Optionally followed by a non-whitespace filter value
 *   - The token extends from the start of the label to the next whitespace
 *     character (or end of string). It includes the filter label and filter
 *     value, if present, but does not include any trailing space after the value.
 *
 * The cursor is considered "inside" the token when
 * `startIndex <= cursorPosition <= endIndex`.
 *
 * Filters are checked in array order. In practice the cursor can only be
 * inside one token at a time (tokens are space-separated), but if two
 * filter labels could theoretically match the same position (e.g. if there
 * are two labels that start with the same string, like `repo:` and `repo-name:`),
 * the first filter in the array wins.
 * Returns `null` if the cursor is not inside any filter token.
 */
export function getActiveFilter(
  query: string,
  cursorPosition: number,
  filters: SearchFilter[]
): ActiveFilter | null {
  if (!query || cursorPosition < 0 || cursorPosition > query.length) {
    return null;
  }

  // Filter labels (e.g. repo:, language:, path:) don't contain regex
  // special characters, so no escaping is needed.
  const labelPattern = filters.map(f => f.label).join('|');

  // Build a regex that matches any known filter token in the query.
  // Lookbehind (?<= ) ensures we only match at the start of a "word",
  // avoiding false positives on substrings like "myrepo:" or "language:repo:".
  // \S* captures the optional filter value after the label (up to the next space).
  // e.g. for filters [repo:, language:], this matches "repo:frontbucket"
  // or "language:" anywhere in the query.
  const tokenRegex = new RegExp(`(?:^|(?<= ))(${labelPattern})\\S*`, 'g');

  for (const match of query.matchAll(tokenRegex)) {
    // match.index is always defined when using matchAll (which requires
    // the global flag), but TypeScript types it as possibly undefined.
    const { index: tokenStart } = match;
    if (tokenStart === undefined) {
      continue;
    }

    const tokenEnd = tokenStart + match[0].length; // match[0] is the full matched string (e.g. "repo:frontbucket")
    const label = match[1]; // match[1] is the first capture group (e.g. "repo:")

    // Check if the cursor falls within this token (inclusive on both edges).
    // e.g. for "repo:" at positions 0-5, cursor at 0, 3, or 5 all match.
    if (cursorPosition >= tokenStart && cursorPosition <= tokenEnd) {
      const filter = filters.find(f => f.label === label)!;
      return {
        filter,
        value: match[0].slice(label.length),
        startIndex: tokenStart,
        endIndex: tokenEnd,
      };
    }
  }

  return null;
}
